import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Paper, Button, Chip, Stack, CircularProgress } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import { getAudioPracticeDetails } from '../services/api';
import FeedbackBlock from '../components/blocks/FeedbackBlock';

interface AudioPractice {
    id: number;
    title: string;
    description: string;
    duration: string;
    audio_url: string | null;
    image_url: string | null;
    category: string;
    author: string | null;
    created_at: string;
    is_premium: boolean;
    tags: string[];
}

const AudioPracticeDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [practice, setPractice] = useState<AudioPractice | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPracticeDetails = async () => {
            if (!id) return;
            
            try {
                setIsLoading(true);
                const data = await getAudioPracticeDetails(parseInt(id));
                setPractice(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке данных');
            } finally {
                setIsLoading(false);
            }
        };

        fetchPracticeDetails();
    }, [id]);

    const togglePlay = () => {
        setIsPlaying(!isPlaying);
    };

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                    <Typography color="error" gutterBottom>
                        {error}
                    </Typography>
                    <Button variant="contained" onClick={() => navigate('/')}>
                        Вернуться на главную
                    </Button>
                </Paper>
            </Container>
        );
    }

    if (!practice) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                    <Typography gutterBottom>
                        Аудио-практика не найдена
                    </Typography>
                    <Button variant="contained" onClick={() => navigate('/')}>
                        Вернуться на главную
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                    {/* Image Section */}
                    <Box sx={{ flex: 1 }}>
                        {practice.image_url ? (
                            <img 
                                src={practice.image_url} 
                                alt={practice.title}
                                style={{ 
                                    width: '100%', 
                                    height: 'auto', 
                                    borderRadius: '8px',
                                    maxHeight: '400px',
                                    objectFit: 'cover'
                                }}
                            />
                        ) : (
                            <Box 
                                sx={{ 
                                    width: '100%', 
                                    height: '300px', 
                                    bgcolor: 'grey.200',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography variant="h6" color="text.secondary">
                                    Изображение отсутствует
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            {practice.title}
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <Chip 
                                icon={<AccessTimeIcon />} 
                                label={practice.duration || 'Длительность не указана'} 
                                variant="outlined" 
                            />
                            {practice.author && (
                                <Chip 
                                    icon={<PersonIcon />} 
                                    label={practice.author} 
                                    variant="outlined" 
                                />
                            )}
                        </Stack>

                        {practice.description && (
                            <Typography variant="body1" paragraph>
                                {practice.description}
                            </Typography>
                        )}

                        {/* Audio Player */}
                        <Box sx={{ 
                            mt: 4, 
                            p: 2, 
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: 1
                        }}>
                            {practice.audio_url ? (
                                <>
                                    <Button
                                        variant="contained"
                                        startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                                        onClick={togglePlay}
                                        sx={{ mb: 2 }}
                                    >
                                        {isPlaying ? 'Пауза' : 'Слушать'}
                                    </Button>
                                    
                                    {/* Progress bar */}
                                    <Box sx={{ 
                                        width: '100%', 
                                        height: 4, 
                                        bgcolor: 'grey.200',
                                        borderRadius: 2,
                                        overflow: 'hidden'
                                    }}>
                                        <Box sx={{ 
                                            width: `${(currentTime / 900) * 100}%`, // 900 seconds = 15 minutes
                                            height: '100%',
                                            bgcolor: 'primary.main'
                                        }} />
                                    </Box>
                                </>
                            ) : (
                                <Typography color="text.secondary">
                                    Аудиофайл недоступен
                                </Typography>
                            )}
                        </Box>

                        {/* Tags */}
                        {practice.tags.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Теги:
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {practice.tags.map((tag, index) => (
                                        <Chip 
                                            key={index} 
                                            label={tag} 
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Feedback Block */}
                        <FeedbackBlock 
                            onSubmit={(feedback) => {
                                console.log('Feedback submitted:', feedback);
                                // TODO: Implement feedback submission to API
                            }}
                        />
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default AudioPracticeDetail; 